import { ActionButton, Body, Title } from '@icarros/ids-react';
import { ContainerNps, EmptyContainer } from './style';
import { MultipleEdits, SaveOnBack, WarningOnBack } from './components/Modal/';
import React, { ReactElement, useEffect, useState } from 'react';

import { AdActions } from '../../../store/actions/ad';
import { ReactComponent as CarDocument } from '../../../../assets/rebranding/illustrations/documento-carro.svg';
import { ChassiForm } from './ChassiForm';
import { ColorsForm } from './ColorsForm';
import { DescriptionForm } from './DescriptionForm';
import { DifferentialsForm } from './DifferentialsForm';
import { DoorsForm } from './DoorsForm';
import { EDIT_ROUTES } from '../../../utils/adEditRoutes';
import { FabricationYearForm } from './FabricationYearForm';
import { FuelsForm } from './FuelsForm';
import { GearForm } from './GearForm';
import { KmForm } from './KmForm';
import { ModelYearForm } from './ModelYearForm';
import { OptionalsForm } from './OptionalsForm';
import { PhotosForm } from './PhotosForm';
import { PriceForm } from './PriceForm';
import { SideContainerWrapper } from '../../../styles/sideContainerStyles';
import { VersionsForm } from './VersionsForm';
import { useAdContext } from '../../../store/contexts/ad';
import { useHistory } from 'react-router-dom';
import { useWebappFlags } from '../../../hooks/useWebAppFlags';

export function EditFields(): JSX.Element {
  const history = useHistory();
  const {
    openEditView,
    valueIsDifferentFromCurrent,
    setTransition,
    transition,
    currentWarningMessage,
    cumulativeChanges,
    setCurrentTabDepth,
    currentTabDepth,
    modalInfoChassi,
    setModalInfoChassi
  } = useAdContext();
  const { invalidatePricesCache } = AdActions();
  const [modalOpen, setModalOpen] = useState(false);
  const [warningModal, setWarningModal] = useState(false);
  const [multipleEditModal, setMultipleEditModal] = useState(false);
  const { getFlagOnSession } = useWebappFlags();

  const baseUrl = openEditView
    ? location.pathname.replace('/' + openEditView, '').replace('meu-icarros/', '')
    : location.pathname;

  useEffect(() => {
    setTransition(false);
  }, [openEditView, setTransition]);

  function closeEditPageWithTransition() {
    setTransition(true);
    setTimeout(() => {
      history.push(baseUrl);
    }, 300);
  }

  function handleClose() {
    if (currentTabDepth > 0) {
      setCurrentTabDepth(state => {
        if (state > 0) return state - 1;
        return state;
      });
      return history.goBack();
    }

    if (cumulativeChanges && currentWarningMessage && !valueIsDifferentFromCurrent) {
      return closeEditPageWithTransition();
    }

    if (cumulativeChanges && currentWarningMessage) {
      return setMultipleEditModal(true);
    }

    if (currentWarningMessage) {
      return setWarningModal(true);
    }

    if (valueIsDifferentFromCurrent) {
      return setModalOpen(true);
    }

    return closeEditPageWithTransition();
  }

  function getCurrentView(currentRoute?: string): ReactElement {
    switch (currentRoute) {
      case EDIT_ROUTES.modelYear.name:
        return <ModelYearForm />;
      case EDIT_ROUTES.fabricationYear.name:
        return <FabricationYearForm />;
      case EDIT_ROUTES.version.name:
        return <VersionsForm />;
      case EDIT_ROUTES.colors.name:
        return <ColorsForm />;
      case EDIT_ROUTES.doors.name:
        return <DoorsForm />;
      case EDIT_ROUTES.fuel.name:
        return <FuelsForm />;
      case EDIT_ROUTES.price.name:
        return <PriceForm />;
      case EDIT_ROUTES.km.name:
        return <KmForm />;
      case EDIT_ROUTES.optionals.name:
        return <OptionalsForm />;
      case EDIT_ROUTES.differentials.name:
        return <DifferentialsForm />;
      case EDIT_ROUTES.description.name:
        return <DescriptionForm />;
      case EDIT_ROUTES.photos.name:
        return <PhotosForm />;
      case EDIT_ROUTES.gear.name:
        return <GearForm />;
      case EDIT_ROUTES.chassi.name:
        return getFlagOnSession('validacaoChassi') ? (
          <ChassiForm
            onClose={handleClose}
            modalInfoChassi={modalInfoChassi}
            setModalInfoChassi={setModalInfoChassi}
          />
        ) : (
          <></>
        );
      default:
        return <></>;
    }
  }

  return (
    <SideContainerWrapper isOpen={!!openEditView} isTransition={transition}>
      {openEditView && (
        <ActionButton
          variant="neutral"
          weight="small"
          text="voltar"
          iconName="itaufonts_seta"
          onClick={() => handleClose()}
        />
      )}

      {!openEditView && (
        <ContainerNps>
          <EmptyContainer>
            <CarDocument />
            <Title size="overline">você está no</Title>
            <Title size="base" contrast="light">
              gerenciamento do anúncio
            </Title>
            <Body size="body_2" variant="variant">
              Acesse as opções na lateral para exibir ou editar as informações do seu anúncio.
            </Body>
          </EmptyContainer>
        </ContainerNps>
      )}

      {getCurrentView(openEditView)}

      {modalOpen && (
        <SaveOnBack
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          closeEditPage={closeEditPageWithTransition}
        />
      )}

      {warningModal && currentWarningMessage && (
        <WarningOnBack
          modalOpen={warningModal}
          setModalOpen={setWarningModal}
          closeEditPage={closeEditPageWithTransition}
          title={currentWarningMessage.title}
          body={currentWarningMessage.body}
        />
      )}

      {multipleEditModal && currentWarningMessage && (
        <MultipleEdits
          modalOpen={multipleEditModal}
          setModalOpen={setMultipleEditModal}
          title={currentWarningMessage.title}
          body={currentWarningMessage.body}
          secondaryButtonText="não salvar"
          handleBack={() => {
            invalidatePricesCache();
            closeEditPageWithTransition();
          }}
          handleContinue={() => {
            if (currentWarningMessage.redirect) {
              setCurrentTabDepth(state => state + 1);
              invalidatePricesCache();
              history.push(currentWarningMessage.redirect);
            }
          }}
        />
      )}
    </SideContainerWrapper>
  );
}
